import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import Layout from "@/components/layout/Layout";
import { StatusPill } from "@/components/StatusPill";
import { PolicySubstatusText } from "@/components/insured/policies/PolicySubstatusText";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, Pencil, Search, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  usePolicies,
  usePoliciesStats,
  useCreatePolicy,
  useInsuranceCompanies,
  useFlintProducers,
  useFeatureFlag,
  exportPoliciesCsv,
} from "@/lib/api";
import { SearchableSelect } from "@/components/searchable-select";
import { InsuredPicker } from "@/components/InsuredPicker";
import { useAgencyContext } from "@/lib/agencyContext";
import { FilterChip } from "@/components/FilterChip";
import { useToast } from "@/hooks/use-toast";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatPolicyNumber, formatDollar, unformatDollar, formatDate } from "@/lib/formatters";
import { getPolicyErrors, hasErrors, scrollToFirstError } from "@/lib/validation";
import { FieldError } from "@/components/FieldError";
import {
  POLICY_STATUS_OPTIONS,
  POLICY_SUBSTATUS_OPTIONS,
} from "@/components/insured/policies/policy-status";
import { isPolicySubstatus } from "@shared/policy-substatus";
import { DateFilter } from "@/components/list-filters/DateFilter";
import { FilterField, FiltersButton, FiltersPanel } from "@/components/list-filters/ListFilters";
import { trackEvent } from "@/lib/trackEvent";
import type { DateFilterQuery } from "@shared/list-filters";
import type { PolicyStatusLiteral } from "@shared/pagination";
import { FLAG_POLICIES_INSUREDS_CSV_EXPORT } from "@shared/feature-flags";

const POLICY_TYPES = [
  "Commercial Auto",
  "General Liability",
  "Cargo",
  "Physical Damage",
  "Workers Compensation",
  "Umbrella",
  "Motor Truck Cargo",
  "Non-Trucking Liability",
];

const initialPolicyForm = {
  policyNumber: "",
  insuredId: "",
  customerName: "",
  carrier: "",
  mga: "",
  type: "",
  status: "active",
  substatus: "",
  premium: "",
  producer: "",
  effectiveDate: "",
  expirationDate: "",
};

function formatPremiumShort(premium: string | number | null | undefined): string {
  const num = Number(premium);
  if (!premium || isNaN(num)) return "—";
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}k`;
  return `$${num.toLocaleString()}`;
}

type ChipFilter = "inForce" | "expiring" | "lost";
const VALID_CHIP_FILTERS: ChipFilter[] = ["inForce", "expiring", "lost"];
const ALL = "__all__";
const SUBSTATUS_NONE = "__none__";

function readDateFilter(params: URLSearchParams, prefix: string): DateFilterQuery | undefined {
  const exact = params.get(prefix) || undefined;
  const from = params.get(`${prefix}From`) || undefined;
  const to = params.get(`${prefix}To`) || undefined;
  return exact ? { exact } : from && to ? { from, to } : undefined;
}

export default function Policies() {
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;
  const { data: insuranceCompanies } = useInsuranceCompanies();
  const { data: flintProducers } = useFlintProducers();
  const producerOptions = useMemo(
    () =>
      (flintProducers ?? []).map((p) => ({
        value: p.fullName,
        label: p.fullName,
      })),
    [flintProducers],
  );
  const createPolicy = useCreatePolicy();
  const csvExportEnabled = useFeatureFlag(FLAG_POLICIES_INSUREDS_CSV_EXPORT);
  const { toast } = useToast();

  const [, navigate] = useLocation();
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  // CSR-674: pagination/filter state lives in the URL so /policies pages
  // are deep-linkable and reload-safe.
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const rawStatus = params.get("status");
  const rawBucket = params.get("bucket") ?? rawStatus;
  const chipFilter: ChipFilter | null =
    rawBucket && (VALID_CHIP_FILTERS as string[]).includes(rawBucket) ? (rawBucket as ChipFilter) : null;
  const advancedStatus =
    rawStatus && POLICY_STATUS_OPTIONS.some((option) => option.value === rawStatus)
      ? (rawStatus as PolicyStatusLiteral)
      : undefined;
  const rawSubstatus = params.get("substatus");
  const substatus = isPolicySubstatus(rawSubstatus) ? rawSubstatus : undefined;
  const carrier = params.get("carrier") || undefined;
  const mga = params.get("mga") || undefined;
  const parentCarrier = params.get("parentCarrier") || undefined;
  const effectiveDate = readDateFilter(params, "effectiveDate");
  const expirationDate = readDateFilter(params, "expirationDate");
  const cancellationDate = readDateFilter(params, "cancellationDate");
  const qParam = params.get("q") || "";
  const pageSize = 25;

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 250);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  // setSearchParam: write a single query param, optionally resetting page.
  // Wouter's `useLocation` setter accepts the path (including `?...`).
  const setSearchParam = (key: string, value: string | null, resetPage = false) => {
    const next = new URLSearchParams(search);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (resetPage) next.delete("page");
    const qs = next.toString();
    navigate(qs ? `/policies?${qs}` : "/policies", { replace: false });
  };

  const setSearchParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    next.delete("page");
    const qs = next.toString();
    navigate(qs ? `/policies?${qs}` : "/policies", { replace: false });
  };

  // Sync debounced search input → URL `q` param. Resets page.
  useEffect(() => {
    if (debouncedQ === qParam) return;
    setSearchParam("q", debouncedQ || null, /*resetPage*/ true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  // Keep input in sync when URL changes externally (e.g., back button).
  useEffect(() => {
    setQInput(qParam);
  }, [qParam]);

  const {
    data: policiesResponse,
    isLoading,
    isPlaceholderData,
  } = usePolicies({
    agencyId,
    page,
    pageSize,
    q: debouncedQ || undefined,
    bucket: chipFilter || undefined,
    advancedStatus,
    substatus,
    carrier,
    mga,
    parentCarrier,
    effectiveDate,
    expirationDate,
    cancellationDate,
  });
  const { data: chipStats } = usePoliciesStats(agencyId);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [tried, setTried] = useState(false);
  const [newPolicy, setNewPolicy] = useState(initialPolicyForm);

  const carrierOptions = useMemo(() => {
    const names = (insuranceCompanies?.insuranceCarriers ?? []).map((c) => c.name);
    const priorityOrder = ["Progressive", "Geico", "CoverWhale"];
    const priority = priorityOrder.filter((c) => names.includes(c));
    const rest = names.filter((c) => !priorityOrder.includes(c)).sort((a, b) => a.localeCompare(b));
    return [...priority, ...rest].map((name) => ({ value: name, label: name }));
  }, [insuranceCompanies]);

  const mgaOptions = useMemo(
    () =>
      (insuranceCompanies?.mgas ?? []).map((m) => ({
        value: m.name,
        label: m.name,
      })),
    [insuranceCompanies],
  );
  const parentCarrierOptions = useMemo(
    () =>
      (insuranceCompanies?.parentCompanies ?? []).map((company) => ({
        value: company.name,
        label: company.name,
      })),
    [insuranceCompanies],
  );

  const activeFilterCount = [
    chipFilter,
    advancedStatus,
    substatus,
    carrier,
    mga,
    parentCarrier,
    effectiveDate,
    expirationDate,
    cancellationDate,
  ].filter(Boolean).length;

  useEffect(() => {
    trackEvent("Policies List Viewed", {
      default_filter_state: activeFilterCount === 0,
    });
    // Page-view baseline is intentionally once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debouncedQ && activeFilterCount > 0) {
      trackEvent("Search Used With Filters", {
        page: "Policies",
        has_active_filters: true,
      });
    }
  }, [debouncedQ, activeFilterCount]);

  const errors = getPolicyErrors(newPolicy);
  const errClass = (field: string) => (tried && errors[field] ? "border-destructive" : "");

  const handleCreatePolicy = async () => {
    setTried(true);
    if (hasErrors(errors)) {
      scrollToFirstError();
      return;
    }

    try {
      await createPolicy.mutateAsync({
        policyNumber: newPolicy.policyNumber,
        insuredId: newPolicy.insuredId || null,
        customerName: newPolicy.customerName,
        carrier: newPolicy.carrier,
        mga: newPolicy.mga.trim() || null,
        type: newPolicy.type || null,
        status: newPolicy.status,
        // CSR-2336 — only sent when chosen, so an unset substatus adds no key.
        ...(isPolicySubstatus(newPolicy.substatus) ? { substatus: newPolicy.substatus } : {}),
        premium: newPolicy.premium ? unformatDollar(newPolicy.premium) : null,
        producer: newPolicy.producer || null,
        effectiveDate: newPolicy.effectiveDate,
        expirationDate: newPolicy.expirationDate,
      });

      toast({ title: "Success", description: "Policy created successfully" });
      setIsAddDialogOpen(false);
      setTried(false);
      setNewPolicy(initialPolicyForm);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create policy",
        variant: "destructive",
      });
    }
  };

  // CSR-674: server returns one page; client renders it directly.
  const policies = policiesResponse?.data || [];
  const total = policiesResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const setChipFilter = (next: ChipFilter | null) => {
    setSearchParams({
      bucket: next,
      status: rawStatus && (VALID_CHIP_FILTERS as string[]).includes(rawStatus) ? null : rawStatus,
    });
    trackEvent("Quick Filter Selected", { page: "Policies", pill_name: next });
  };
  const setPage = (next: number) => setSearchParam("page", next > 1 ? String(next) : null);

  const toggleFilters = () => {
    const next = !filtersOpen;
    setFiltersOpen(next);
    trackEvent("Filters Panel Toggled", {
      page: "Policies",
      state: next ? "open" : "closed",
    });
  };
  const focusFiltersButton = useCallback(() => filtersButtonRef.current?.focus(), []);

  const changeAdvanced = (name: string, value: string | null) => {
    setSearchParam(name, value, true);
    trackEvent("Advanced Filter Changed", {
      page: "Policies",
      filter_name: name,
    });
  };

  const changeDate = (prefix: string, value: DateFilterQuery | undefined, mode: "specific" | "between") => {
    setSearchParams({
      [prefix]: value?.exact ?? null,
      [`${prefix}From`]: value?.from ?? null,
      [`${prefix}To`]: value?.to ?? null,
    });
    trackEvent("Advanced Filter Changed", {
      page: "Policies",
      filter_name: prefix,
      mode,
    });
  };

  const clearFilters = () => {
    setQInput("");
    setSearchParams({
      bucket: null,
      status: null,
      substatus: null,
      carrier: null,
      mga: null,
      parentCarrier: null,
      effectiveDate: null,
      effectiveDateFrom: null,
      effectiveDateTo: null,
      expirationDate: null,
      expirationDateFrom: null,
      expirationDateTo: null,
      cancellationDate: null,
      cancellationDateFrom: null,
      cancellationDateTo: null,
      q: null,
    });
    trackEvent("Filter Cleared", {
      page: "Policies",
      filters_cleared_count: activeFilterCount,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportPoliciesCsv({
        agencyId,
        q: debouncedQ || undefined,
        bucket: chipFilter || undefined,
        advancedStatus,
        substatus,
        carrier,
        mga,
        parentCarrier,
        effectiveDate,
        expirationDate,
        cancellationDate,
      });
      if (result.kind === "empty") {
        toast({
          title: "Nothing to export",
          description: "No policies match the current filters.",
        });
        trackEvent("CSV Export Empty", {
          page: "Policies",
          active_filter_count: activeFilterCount,
        });
        return;
      }
      trackEvent("CSV Exported", {
        page: "Policies",
        result_count: total,
        active_filter_count: activeFilterCount,
        has_search: Boolean(debouncedQ),
      });
      if (activeFilterCount >= 2) {
        trackEvent("Filtered List Refined", {
          page: "Policies",
          filter_types_used: activeFilterCount,
          exported: true,
        });
      }
    } catch {
      toast({
        title: "Export failed",
        description: "Please try again.",
        variant: "destructive",
      });
      trackEvent("CSV Export Failed", {
        page: "Policies",
        result_count: total,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col gap-6" style={{ height: "calc(100vh - 7rem)" }}>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 shrink-0">
          <div>
            <h1 className="text-2xl font-extrabold font-heading text-foreground">Policies</h1>
            <p className="text-muted-foreground mt-1">View and manage all insurance policies.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <FiltersButton
              ref={filtersButtonRef}
              open={filtersOpen}
              activeCount={activeFilterCount}
              onClick={toggleFilters}
            />
            {activeFilterCount > 0 ? (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                Clear
              </Button>
            ) : null}
            {csvExportEnabled ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={exporting}
                  aria-busy={exporting}
                  onClick={handleExport}
                  data-testid="button-export-policies"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {exporting ? "Exporting…" : "Export CSV"}
                </Button>
                <span className="sr-only" role="status" aria-live="polite">
                  {exporting ? "Exporting policies CSV" : ""}
                </span>
              </>
            ) : null}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-2xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all font-semibold"
                  data-testid="button-new-policy"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Policy
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                  <DialogTitle>Create New Policy</DialogTitle>
                  <DialogDescription>Manually add a new insurance policy.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="policyNumber">Policy Number *</Label>
                      <Input
                        id="policyNumber"
                        value={newPolicy.policyNumber}
                        onChange={(e) =>
                          setNewPolicy({
                            ...newPolicy,
                            policyNumber: formatPolicyNumber(e.target.value),
                          })
                        }
                        placeholder="POL-12345"
                        maxLength={30}
                        aria-invalid={tried && errors.policyNumber}
                        className={errClass("policyNumber")}
                        data-testid="input-policy-number"
                      />
                      <FieldError show={tried && errors.policyNumber} message="Policy number is required" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="insured">Insured *</Label>
                      <InsuredPicker
                        selectedId={newPolicy.insuredId || null}
                        insuredName={newPolicy.customerName}
                        agencyId={agencyId}
                        invalid={tried && errors.customerName}
                        onSelect={(ins) =>
                          setNewPolicy({
                            ...newPolicy,
                            insuredId: ins.id,
                            customerName: ins.name,
                          })
                        }
                        onClear={() =>
                          setNewPolicy({
                            ...newPolicy,
                            insuredId: "",
                            customerName: "",
                          })
                        }
                      />
                      <FieldError show={tried && errors.customerName} message="Insured is required" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="carrier">Carrier *</Label>
                      <SearchableSelect
                        id="carrier"
                        value={newPolicy.carrier}
                        onChange={(v) => setNewPolicy({ ...newPolicy, carrier: v })}
                        options={carrierOptions}
                        allowCustom
                        placeholder="Select or type a carrier…"
                        className={errClass("carrier")}
                      />
                      {/* Test-only handle: drives state without going through Radix
                        popover (hard to drive in happy-dom). sr-only + onChange
                        keeps React's controlled-input contract intact. */}
                      <input
                        type="text"
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="input-carrier"
                        value={newPolicy.carrier}
                        onChange={(e) =>
                          setNewPolicy({
                            ...newPolicy,
                            carrier: e.target.value,
                          })
                        }
                      />
                      <FieldError show={tried && errors.carrier} message="Carrier is required" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="policyType">Policy Type</Label>
                      <Select
                        value={newPolicy.type}
                        onValueChange={(value) => setNewPolicy({ ...newPolicy, type: value })}
                      >
                        <SelectTrigger id="policyType" data-testid="select-policy-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {POLICY_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={newPolicy.status}
                        onValueChange={(value) => setNewPolicy({ ...newPolicy, status: value })}
                      >
                        <SelectTrigger id="status" data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {POLICY_STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="premium">Premium</Label>
                      <Input
                        id="premium"
                        value={newPolicy.premium}
                        onChange={(e) =>
                          setNewPolicy({
                            ...newPolicy,
                            premium: formatDollar(e.target.value),
                          })
                        }
                        placeholder="$10,000"
                        data-testid="input-premium"
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="substatus">Substatus</Label>
                    <Select
                      value={newPolicy.substatus || SUBSTATUS_NONE}
                      onValueChange={(value) =>
                        setNewPolicy({ ...newPolicy, substatus: value === SUBSTATUS_NONE ? "" : value })
                      }
                    >
                      <SelectTrigger id="substatus" data-testid="select-substatus">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SUBSTATUS_NONE}>None</SelectItem>
                        {POLICY_SUBSTATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="producer">Producer</Label>
                      <SearchableSelect
                        value={newPolicy.producer}
                        onChange={(v) => setNewPolicy({ ...newPolicy, producer: v })}
                        options={producerOptions}
                        allowCustom
                        placeholder="Select or type a producer…"
                      />
                      {/* Test-only handle: drives state without going through Radix
                        popover (hard to drive in happy-dom). sr-only + onChange
                        keeps React's controlled-input contract intact. */}
                      <input
                        type="text"
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="input-producer"
                        value={newPolicy.producer}
                        onChange={(e) =>
                          setNewPolicy({
                            ...newPolicy,
                            producer: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="mga">MGA</Label>
                      <SearchableSelect
                        id="mga"
                        value={newPolicy.mga}
                        onChange={(v) => setNewPolicy({ ...newPolicy, mga: v })}
                        options={mgaOptions}
                        allowCustom
                        placeholder="Select or type an MGA…"
                      />
                      {/* Test-only handle: mirrors the producer pattern so component
                        tests can drive state without Radix's popover. */}
                      <input
                        type="text"
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="input-mga"
                        value={newPolicy.mga}
                        onChange={(e) => setNewPolicy({ ...newPolicy, mga: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="effectiveDate">Effective Date *</Label>
                      <Input
                        id="effectiveDate"
                        type="date"
                        value={newPolicy.effectiveDate}
                        onChange={(e) => {
                          const effective = e.target.value;
                          const updates: Partial<typeof newPolicy> = {
                            effectiveDate: effective,
                          };
                          if (effective) {
                            const d = new Date(effective + "T00:00:00");
                            d.setFullYear(d.getFullYear() + 1);
                            updates.expirationDate = d.toISOString().split("T")[0];
                          }
                          setNewPolicy({ ...newPolicy, ...updates });
                        }}
                        aria-invalid={tried && errors.effectiveDate}
                        className={errClass("effectiveDate")}
                        data-testid="input-effective-date"
                      />
                      <FieldError show={tried && errors.effectiveDate} message="Effective date is required" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="expirationDate">Expiration Date *</Label>
                      <Input
                        id="expirationDate"
                        type="date"
                        value={newPolicy.expirationDate}
                        onChange={(e) =>
                          setNewPolicy({
                            ...newPolicy,
                            expirationDate: e.target.value,
                          })
                        }
                        aria-invalid={tried && errors.expirationDate}
                        className={errClass("expirationDate")}
                        data-testid="input-expiration-date"
                      />
                      <FieldError
                        show={tried && errors.expirationDate}
                        message={
                          !newPolicy.expirationDate
                            ? "Expiration date is required"
                            : "Expiration date must be after effective date"
                        }
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePolicy}
                    disabled={createPolicy.isPending}
                    data-testid="button-submit-policy"
                  >
                    {createPolicy.isPending ? "Creating..." : "Create Policy"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <FiltersPanel open={filtersOpen} onCloseFocus={focusFiltersButton}>
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip
                label="In Force"
                count={chipStats?.inForce.count ?? 0}
                unit="policy"
                premium={chipStats?.inForce.premium ?? 0}
                selected={chipFilter === "inForce"}
                onClick={() => setChipFilter(chipFilter === "inForce" ? null : "inForce")}
                accent="green"
                tooltip="Policies currently active — within their effective period and not yet expired."
                data-testid="chip-in-force"
              />
              <FilterChip
                label="Expiring Soon"
                count={chipStats?.expiring.count ?? 0}
                unit="policy"
                premium={chipStats?.expiring.premium ?? 0}
                selected={chipFilter === "expiring"}
                onClick={() => setChipFilter(chipFilter === "expiring" ? null : "expiring")}
                accent="yellow"
                tooltip="Active policies that expire within the next 60 days."
                data-testid="chip-expiring"
              />
              <FilterChip
                label="Recently Lost"
                count={chipStats?.lost.count ?? 0}
                unit="policy"
                premium={chipStats?.lost.premium ?? 0}
                selected={chipFilter === "lost"}
                onClick={() => setChipFilter(chipFilter === "lost" ? null : "lost")}
                accent="red"
                tooltip="Policies that expired or were cancelled in the last 60 days."
                data-testid="chip-lost"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FilterField label="Status">
                <Select
                  value={advancedStatus ?? ALL}
                  onValueChange={(value) => changeAdvanced("status", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Policy status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    {POLICY_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Substatus">
                <Select
                  value={substatus ?? ALL}
                  onValueChange={(value) => changeAdvanced("substatus", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Policy substatus" data-testid="filter-substatus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All substatuses</SelectItem>
                    {POLICY_SUBSTATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Insurance Carrier">
                <Select
                  value={carrier ?? ALL}
                  onValueChange={(value) => changeAdvanced("carrier", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Insurance carrier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All carriers</SelectItem>
                    {carrierOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="MGA">
                <Select
                  value={mga ?? ALL}
                  onValueChange={(value) => changeAdvanced("mga", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="MGA">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All MGAs</SelectItem>
                    {mgaOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Parent Carrier">
                <Select
                  value={parentCarrier ?? ALL}
                  onValueChange={(value) => changeAdvanced("parentCarrier", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Parent carrier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All parent carriers</SelectItem>
                    {parentCarrierOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <DateFilter
                label="Effective Date"
                value={effectiveDate}
                onChange={(value, mode) => changeDate("effectiveDate", value, mode)}
              />
              <DateFilter
                label="Expiration Date"
                value={expirationDate}
                onChange={(value, mode) => changeDate("expirationDate", value, mode)}
              />
              <DateFilter
                label="Cancelled At"
                value={cancellationDate}
                onChange={(value, mode) => changeDate("cancellationDate", value, mode)}
              />
            </div>
          </div>
        </FiltersPanel>

        <div className="flex justify-end shrink-0">
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Search policies..."
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              className="pl-9"
              data-testid="input-search-policies"
            />
          </div>
        </div>

        {isLoading && !isPlaceholderData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Card className="border-border shadow-none overflow-hidden flex-1 flex flex-col min-h-0">
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-muted-foreground uppercase bg-muted border-b border-border sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Policy Number</th>
                    <th className="px-6 py-4 font-semibold">Type</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Insured</th>
                    <th className="px-6 py-4 font-semibold">Carrier</th>
                    {/* CSR-748: Effective and Expiration dates collapse on mobile to
                        keep the table from forcing horizontal scroll. */}
                    <th className="px-6 py-4 font-semibold hidden md:table-cell">Effective</th>
                    <th className="px-6 py-4 font-semibold hidden md:table-cell">Expiration</th>
                    <th className="px-6 py-4 font-semibold">Premium</th>
                    <th className="px-6 py-4 w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {policies.map((policy) => (
                    <tr
                      key={policy.id}
                      onClick={() => navigate(`/policies/${policy.id}`)}
                      className="hover:bg-muted/30 transition-colors cursor-pointer"
                      data-testid={`row-policy-${policy.id}`}
                    >
                      <td
                        className="px-6 py-4 font-semibold text-foreground"
                        data-testid={`text-policy-number-${policy.id}`}
                      >
                        {policy.policyNumber}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">{policy.type || "—"}</td>
                      <td className="px-6 py-4">
                        <StatusPill status={policy.status} data-testid={`badge-status-${policy.id}`} />
                        <PolicySubstatusText
                          substatus={policy.substatus}
                          className="mt-1"
                          data-testid={`text-substatus-${policy.id}`}
                        />
                      </td>
                      {/* CSR-748: prefer the joined insured name (FK) and fall back to
                          the legacy free-text customerName for orphan policies. */}
                      <td className="px-6 py-4 font-medium" data-testid={`text-insured-${policy.id}`}>
                        {policy.insuredName ?? policy.customerName ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">{policy.carrier}</td>
                      <td
                        className="px-6 py-4 text-muted-foreground hidden md:table-cell"
                        data-testid={`text-effective-${policy.id}`}
                      >
                        {formatDate(policy.effectiveDate)}
                      </td>
                      <td
                        className="px-6 py-4 text-muted-foreground hidden md:table-cell"
                        data-testid={`text-expiration-${policy.id}`}
                      >
                        {formatDate(policy.expirationDate)}
                      </td>
                      <td className="px-6 py-4 font-medium text-amber-600">{formatPremiumShort(policy.premium)}</td>
                      <td className="px-6 py-4">
                        <Pencil className="w-4 h-4 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                  {policies.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-6 py-12 text-center text-muted-foreground"
                        data-testid="text-no-policies"
                      >
                        No policies found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t shrink-0">
                <p className="text-sm text-muted-foreground">
                  {((page - 1) * pageSize + 1).toLocaleString()}&ndash;
                  {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1 || isPlaceholderData}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1 || isPlaceholderData}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-3">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages || isPlaceholderData}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages || isPlaceholderData}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}
